import React, { useState, useEffect } from 'react';
import { Info, CheckCircle, Server, Database, Clock, AlertCircle } from 'lucide-react';
import apiService from '../../services/api';

function TabInfo({ configuracion }) {
  const [estado, setEstado] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchEstado();
  }, []);

  const fetchEstado = async () => {
    try {
      const data = await apiService.get('/sistema/estado');
      setEstado(data);
    } catch (error) {
      console.error('Error al obtener estado:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-ink-200 border-t-ink-800 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-sm text-ink-400">Cargando información...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-6">
        <Info size={24} className="text-ink-700" />
        <div>
          <h2 className="text-xl font-semibold text-ink-900">Información del Sistema</h2>
          <p className="text-sm text-ink-500">Versión, estado y estadísticas</p>
        </div>
      </div>

      {/* Versión del Sistema */}
      <div className="border border-ink-200 rounded-lg p-6 bg-white">
        <h3 className="font-semibold text-ink-900 mb-4 flex items-center gap-2">
          <Server size={18} />
          Versión del Sistema
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-ink-50 rounded-lg p-4">
            <p className="text-sm text-ink-600 mb-1">Versión</p>
            <p className="text-lg font-bold text-ink-900">{estado?.version || '1.0.3'}</p>
          </div>
          <div className="bg-ink-50 rounded-lg p-4">
            <p className="text-sm text-ink-600 mb-1">Entorno</p>
            <p className="text-lg font-bold text-ink-900 capitalize">{estado?.entorno || 'production'}</p>
          </div>
          <div className="bg-ink-50 rounded-lg p-4">
            <p className="text-sm text-ink-600 mb-1">Uptime</p>
            <p className="text-lg font-bold text-ink-900">{estado?.uptimeFormatted || 'N/A'}</p>
          </div>
        </div>
      </div>

      {/* Estado del Servidor */}
      <div className="border border-ink-200 rounded-lg p-6 bg-white">
        <h3 className="font-semibold text-ink-900 mb-4 flex items-center gap-2">
          <CheckCircle size={18} className="text-green-600" />
          Estado del Servidor
        </h3>
        <div className="space-y-3">
          <div className="flex items-center justify-between p-3 bg-ink-50 rounded-lg">
            <span className="text-sm text-ink-600">Base de datos</span>
            <span className="font-medium text-green-600 flex items-center gap-1">
              <CheckCircle size={16} />
              {estado?.bd?.conectada ? 'Conectada' : 'Desconectada'}
            </span>
          </div>
          <div className="flex items-center justify-between p-3 bg-ink-50 rounded-lg">
            <span className="text-sm text-ink-600">Tamaño BD</span>
            <span className="font-medium text-ink-900">{estado?.bd?.tamano_mb?.toFixed(2) || '0'} MB</span>
          </div>
          <div className="flex items-center justify-between p-3 bg-ink-50 rounded-lg">
            <span className="text-sm text-ink-600">Memoria usada</span>
            <span className="font-medium text-ink-900">
              {estado?.memoria?.usado || 0} MB / {estado?.memoria?.total || 0} MB
            </span>
          </div>
        </div>
      </div>

      {/* Estadísticas Generales */}
      <div className="border border-ink-200 rounded-lg p-6 bg-white">
        <h3 className="font-semibold text-ink-900 mb-4 flex items-center gap-2">
          <Database size={18} />
          Estadísticas Generales
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-blue-50 rounded-lg p-4 text-center">
            <p className="text-2xl font-bold text-blue-900">{estado?.estadisticas?.usuarios || 0}</p>
            <p className="text-sm text-blue-600 mt-1">Usuarios</p>
          </div>
          <div className="bg-green-50 rounded-lg p-4 text-center">
            <p className="text-2xl font-bold text-green-900">{estado?.estadisticas?.clientes || 0}</p>
            <p className="text-sm text-green-600 mt-1">Clientes</p>
          </div>
          <div className="bg-purple-50 rounded-lg p-4 text-center">
            <p className="text-2xl font-bold text-purple-900">{estado?.estadisticas?.contenedores || 0}</p>
            <p className="text-sm text-purple-600 mt-1">Contenedores</p>
          </div>
          <div className="bg-orange-50 rounded-lg p-4 text-center">
            <p className="text-2xl font-bold text-orange-900">{estado?.estadisticas?.navieras || 0}</p>
            <p className="text-sm text-orange-600 mt-1">Navieras</p>
          </div>
        </div>
      </div>

      {/* Información Adicional */}
      <div className="border border-ink-200 rounded-lg p-6 bg-white">
        <h3 className="font-semibold text-ink-900 mb-4 flex items-center gap-2">
          <Clock size={18} />
          Información Adicional
        </h3>
        <div className="space-y-2 text-sm">
          {estado?.ultimo_recalculo && (
            <p className="text-ink-600">
              <span className="font-medium">Último recálculo:</span>{' '}
              {new Date(estado.ultimo_recalculo).toLocaleString('es-MX')}
            </p>
          )}
          <p className="text-ink-600">
            <span className="font-medium">Sistema:</span> SistemaDemorasV3 - Dragon Cargo
          </p>
          <p className="text-ink-600">
            <span className="font-medium">Tecnología:</span> React + Node.js + MySQL
          </p>
        </div>
      </div>

      {/* Footer */}
      <div className="text-center pt-6 border-t border-ink-200">
        <p className="text-xs text-ink-500">
          Sistema de Gestión de Demoras - Dragon Cargo
        </p>
        <p className="text-xs text-ink-400 mt-1">
          © 2026 Todos los derechos reservados
        </p>
      </div>
    </div>
  );
}

export default TabInfo;
